"use client";

import { useState } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { Box, Button, HStack, IconButton, Text, Textarea } from "@chakra-ui/react";
import { LuHeart, LuMessageCircle, LuSend } from "react-icons/lu";
import { useMessage } from "@/app/context/MessageProvider";

type BlogLikeApiResponse = {
  success: boolean;
  liked: boolean;
  likesCount: number;
  error?: string;
};

type BlogCommentApiResponse = {
  success: boolean;
  error?: string;
};

type BlogSocialBarProps = {
  slug: string;
  initialLikesCount: number;
  initialLikedByUser: boolean;
  commentsCount?: number;
  commentTargetId?: string;
};

export function BlogSocialBar({
  slug,
  initialLikesCount,
  initialLikedByUser,
  commentsCount = 0,
  commentTargetId = "comments",
}: BlogSocialBarProps) {
  const [likesCount, setLikesCount] = useState(initialLikesCount);
  const [liked, setLiked] = useState(initialLikedByUser);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [showCommentComposer, setShowCommentComposer] = useState(true);
  const [commentText, setCommentText] = useState("");
  const router = useRouter();
  const { status } = useSession();
  const { notify, error } = useMessage();

  const handleLikeClick = () => {
    if (status !== "authenticated") {
      error("Sign in to like this blog post.", "Sign in required");
      return;
    }

    if (isSyncing) return;

    const previousLiked = liked;
    const previousCount = likesCount;
    const nextLiked = !previousLiked;
    const nextCount = Math.max(0, previousCount + (nextLiked ? 1 : -1));

    setLiked(nextLiked);
    setLikesCount(nextCount);
    setIsSyncing(true);

    void (async () => {
      try {
        const response = await fetch(`/api/blog/${slug}/like`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        const payload = (await response.json().catch(() => null)) as
          | BlogLikeApiResponse
          | null;

        if (!response.ok || !payload?.success) {
          setLiked(previousLiked);
          setLikesCount(previousCount);
          notify({
            mode: "error",
            description: payload?.error ?? "Unable to update like right now.",
          });
          return;
        }

        setLiked(payload.liked);
        setLikesCount(payload.likesCount);
      } catch (error) {
        console.error("[BLOG_SOCIAL_BAR_LIKE]", error);
        setLiked(previousLiked);
        setLikesCount(previousCount);
        notify({
          mode: "error",
          description: "Unable to update like right now.",
        });
      } finally {
        setIsSyncing(false);
      }
    })();
  };

  const handleCommentClick = () => {
    const shouldShowComposer = !showCommentComposer;
    setShowCommentComposer(shouldShowComposer);

    if (!shouldShowComposer) return;

    window.requestAnimationFrame(() => {
      const input = document.getElementById(`comment-input-${slug}`);
      if (input instanceof HTMLTextAreaElement) {
        input.focus();
      }
    });

    const target = document.getElementById(commentTargetId);
    if (target instanceof HTMLElement) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleCommentSend = () => {
    if (!commentText.trim() || isSubmittingComment) return;
    if (status !== "authenticated") {
      error("Sign in to comment on this blog post.", "Sign in required");
      return;
    }

    setIsSubmittingComment(true);

    void (async () => {
      try {
        const response = await fetch(`/api/blog/${slug}/comments`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content: commentText.trim() }),
        });
        const payload = (await response.json().catch(() => null)) as BlogCommentApiResponse | null;

        if (!response.ok || !payload?.success) {
          notify({
            mode: "error",
            description: payload?.error ?? "Unable to post comment right now.",
          });
          return;
        }

        notify({
          mode: "regular",
          title: "Comment submitted",
          description: "Your comment is pending review and visible to you until approved.",
        });
        setCommentText("");
        setShowCommentComposer(false);
        router.refresh();
      } catch (submitError) {
        console.error("[BLOG_COMMENT_POST]", submitError);
        notify({
          mode: "error",
          description: "Unable to post comment right now.",
        });
      } finally {
        setIsSubmittingComment(false);
      }
    })();
  };

  return (
    <Box w="100%" maxW="100%">
      <Box w="100%">
        <HStack justify="flex-start" align="center" gap={2} w="100%">
        <Button
          type="button"
          aria-label={liked ? "Unlike this blog post" : "Like this blog post"}
          aria-pressed={liked}
          onClick={handleLikeClick}
          disabled={isSyncing}
          variant="ghost"
          minW="unset"
          h="auto"
          rounded="full"
          px={2}
          py={1}
          bg="whiteAlpha.100"
          _hover={{ bg: "whiteAlpha.200" }}
          _active={{ bg: "whiteAlpha.300" }}
          transition="background-color 140ms ease"
          cursor={isSyncing ? "not-allowed" : "pointer"}
        >
          <HStack gap={1}>
            <Box color={liked ? "#f91880" : "fg.muted"} lineHeight="1">
              <LuHeart fill={liked ? "currentColor" : "none"} />
            </Box>
            <Text
              fontSize="2xs"
              lineHeight="1"
              fontWeight="medium"
              color="whiteAlpha.700"
              fontVariantNumeric="tabular-nums"
              visibility={likesCount > 0 ? "visible" : "hidden"}
            >
              {likesCount > 0 ? likesCount : 0}
            </Text>
          </HStack>
        </Button>

        <Button
          type="button"
          aria-label={showCommentComposer ? "Close comment box" : "Open comment box"}
          aria-pressed={showCommentComposer}
          onClick={handleCommentClick}
          variant="ghost"
          minW="unset"
          h="auto"
          rounded="full"
          px={2}
          py={1}
          bg="whiteAlpha.100"
          cursor="pointer"
          _hover={{ bg: "whiteAlpha.200", cursor: "pointer" }}
          _active={{ bg: "whiteAlpha.300" }}
          transition="background-color 140ms ease"
        >
          <HStack gap={1}>
            <Box color="fg.muted" lineHeight="1">
              <LuMessageCircle />
            </Box>
            <Text
              fontSize="2xs"
              lineHeight="1"
              fontWeight="medium"
              color="whiteAlpha.700"
              fontVariantNumeric="tabular-nums"
              visibility={commentsCount > 0 ? "visible" : "hidden"}
            >
              {commentsCount > 0 ? commentsCount : 0}
            </Text>
          </HStack>
        </Button>
        </HStack>
      </Box>

      <Box
        mt={3}
        w="100%"
        overflow="hidden"
        maxH={showCommentComposer ? "260px" : "0"}
        opacity={showCommentComposer ? 1 : 0}
        transform={showCommentComposer ? "translateY(0)" : "translateY(-6px)"}
        transition="max-height 220ms ease, opacity 180ms ease, transform 220ms ease"
        pointerEvents={showCommentComposer ? "auto" : "none"}
      >
        <Box
          as="form"
          onSubmit={(event) => {
            event.preventDefault();
            handleCommentSend();
          }}
        >
          <Box w="100%">
            <Text fontSize="xs" color="whiteAlpha.700" textAlign="right" mb={1}>
              {commentText.length}/100
            </Text>
            <Box position="relative" w="100%">
            <Textarea
              id={`comment-input-${slug}`}
              value={commentText}
              onChange={(event) => setCommentText(event.target.value)}
              placeholder="Join the conversation"
              maxLength={100}
              rounded="xl"
              bg="whiteAlpha.100"
              borderColor="teal.400"
              color="whiteAlpha.900"
              _placeholder={{ color: "whiteAlpha.600" }}
              _hover={{ borderColor: "teal.400" }}
              _focusVisible={{ borderColor: "teal.400" }}
              resize="vertical"
              minH="96px"
              w="100%"
              pe="46px"
              pb="34px"
            />
            <IconButton
              type="submit"
              aria-label="Send comment"
              size="xs"
              minW="28px"
              h="28px"
              variant="ghost"
              bg="transparent"
              border="none"
              _hover={{ bg: "transparent" }}
              _active={{ bg: "transparent" }}
              transition="color 140ms ease"
              color="white"
              disabled={!commentText.trim() || isSubmittingComment}
              cursor={!commentText.trim() || isSubmittingComment ? "not-allowed" : "pointer"}
              opacity={!commentText.trim() || isSubmittingComment ? 0.55 : 1}
              position="absolute"
              right="2"
              bottom="2"
              zIndex={1}
            >
              <LuSend size={16} />
            </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
